import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import {
  Calendar, Search, Plus, Edit3, Trash2, Star, StarOff,
  Globe, MapPin, Users, TrendingUp, X, CheckCircle,
  Filter, Download, Sparkles, RefreshCw, ExternalLink,
  ChevronLeft, ChevronRight, Eye, EyeOff, Building2,
  Database, AlertCircle, Zap, Link2, Link2Off, StopCircle,
  ScanLine, CheckCheck, Minus, CircleDot, Mail, LayoutGrid
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const getToken = () => localStorage.getItem('access_token') || sessionStorage.getItem('access_token');

const CATEGORIES = [
  'Tümü', 'Teknoloji', 'Otomotiv', 'Gıda & İçecek', 'Moda & Tekstil',
  'Mobilya & Tasarım', 'Sanayi & Makine', 'Sağlık & Medikal',
  'İnşaat & Gayrimenkul', 'Turizm & Seyahat', 'Enerji',
  'Tarım', 'Güvenlik & Savunma', 'Perakende', 'Diğer'
];

const CATEGORY_COLORS = {
  'Teknoloji': 'bg-blue-100 text-blue-700',
  'Otomotiv': 'bg-gray-100 text-gray-700',
  'Gıda & İçecek': 'bg-orange-100 text-orange-700',
  'Moda & Tekstil': 'bg-pink-100 text-pink-700',
  'Mobilya & Tasarım': 'bg-amber-100 text-amber-700',
  'Sanayi & Makine': 'bg-slate-100 text-slate-700',
  'Sağlık & Medikal': 'bg-green-100 text-green-700',
  'İnşaat & Gayrimenkul': 'bg-yellow-100 text-yellow-700',
  'Turizm & Seyahat': 'bg-cyan-100 text-cyan-700',
  'Enerji': 'bg-lime-100 text-lime-700',
  'Tarım': 'bg-emerald-100 text-emerald-700',
  'Güvenlik & Savunma': 'bg-red-100 text-red-700',
  'Perakende': 'bg-violet-100 text-violet-700',
  'Diğer': 'bg-gray-100 text-gray-600',
};

const STATUS_COLORS = {
  published: 'bg-green-100 text-green-700',
  draft: 'bg-yellow-100 text-yellow-700',
  archived: 'bg-gray-100 text-gray-500',
};

function FairModal({ fair, onClose, onSave }) {
  const isEdit = !!fair?.fair_id;
  const [form, setForm] = useState(
    fair || {
      name: '', city: '', country: '', venue: '', start_date: '', end_date: '',
      category: 'Teknoloji', visitors: '', exhibitors: '', description: '',
      website: '', frequency: 'Yıllık', status: 'draft', is_featured: false, image: '',
    }
  );
  const [saving, setSaving] = useState(false);

  const handleChange = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    if (!form.name || !form.city) return;
    setSaving(true);
    try {
      const token = getToken();
      const method = isEdit ? 'PATCH' : 'POST';
      const url = isEdit ? `${API}/ultra/fairs/${form.fair_id}` : `${API}/ultra/fairs`;
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Kayıt başarısız');
      const data = await res.json();
      onSave(data, isEdit);
      onClose();
    } catch (e) {
      alert('Hata: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-xl font-bold text-gray-900">
            {isEdit ? 'Fuarı Düzenle' : 'Yeni Fuar Ekle'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Fuar Adı *</label>
              <input data-testid="fair-name-input" value={form.name} onChange={e => handleChange('name', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="örn. Mobile World Congress (MWC)" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Şehir *</label>
              <input data-testid="fair-city-input" value={form.city} onChange={e => handleChange('city', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="örn. Barcelona" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ülke</label>
              <input value={form.country} onChange={e => handleChange('country', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="örn. İspanya" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Venue / Mekan</label>
              <input value={form.venue} onChange={e => handleChange('venue', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="örn. Fira Barcelona Gran Via" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Başlangıç Tarihi</label>
              <input type="date" value={form.start_date} onChange={e => handleChange('start_date', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bitiş Tarihi</label>
              <input type="date" value={form.end_date} onChange={e => handleChange('end_date', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Kategori</label>
              <select value={form.category} onChange={e => handleChange('category', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                {CATEGORIES.filter(c => c !== 'Tümü').map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sıklık</label>
              <select value={form.frequency} onChange={e => handleChange('frequency', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option>Yıllık</option>
                <option>Yılda iki kez</option>
                <option>2 Yılda bir</option>
                <option>3 Yılda bir</option>
                <option>5 Yılda bir</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ziyaretçi Sayısı</label>
              <input value={form.visitors} onChange={e => handleChange('visitors', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="örn. 100,000+" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Katılımcı / Firma</label>
              <input value={form.exhibitors} onChange={e => handleChange('exhibitors', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="örn. 2,400+" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Açıklama</label>
              <textarea value={form.description} onChange={e => handleChange('description', e.target.value)} rows={3}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Fuarın kısa açıklaması (Türkçe)..." />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
              <input value={form.website} onChange={e => handleChange('website', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="https://..." />
            </div>
            {/* Contact Info - EventsEye'dan çekilen veriler (read-only gösterim) */}
            {isEdit && (form.event_email || form.venue_website || form.venue_email || (form.organizers && form.organizers.length > 0)) && (
              <div className="md:col-span-2 bg-teal-50 border border-teal-100 rounded-xl p-4 space-y-3">
                <p className="text-xs font-bold text-teal-700 uppercase tracking-wide flex items-center gap-2">
                  <Mail className="w-3.5 h-3.5" /> Kontak Bilgileri (EventsEye)
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {form.event_email && (
                    <div>
                      <p className="text-[10px] text-teal-500 font-semibold uppercase">Event E-mail</p>
                      <a href={`mailto:${form.event_email}`} className="text-xs text-teal-800 hover:text-teal-600 font-medium break-all">{form.event_email}</a>
                    </div>
                  )}
                  {form.venue_website && (
                    <div>
                      <p className="text-[10px] text-teal-500 font-semibold uppercase">Venue Website</p>
                      <a href={form.venue_website} target="_blank" rel="noreferrer" className="text-xs text-teal-800 hover:text-teal-600 font-medium break-all">{form.venue_website}</a>
                    </div>
                  )}
                  {form.venue_email && (
                    <div>
                      <p className="text-[10px] text-teal-500 font-semibold uppercase">Venue E-mail</p>
                      <a href={`mailto:${form.venue_email}`} className="text-xs text-teal-800 hover:text-teal-600 font-medium break-all">{form.venue_email}</a>
                    </div>
                  )}
                </div>
                {form.organizers && form.organizers.length > 0 && (
                  <div>
                    <p className="text-[10px] text-teal-500 font-semibold uppercase mb-1.5">Organizatörler ({form.organizers.length})</p>
                    <div className="space-y-1.5">
                      {form.organizers.map((org, i) => (
                        <div key={i} className="bg-white rounded-lg px-3 py-2 flex items-start gap-2 border border-teal-100">
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-gray-800 truncate">{org.name}</p>
                            <div className="flex items-center gap-3 mt-0.5">
                              {org.website && <a href={org.website} target="_blank" rel="noreferrer" className="text-[10px] text-blue-600 hover:underline truncate max-w-[120px]">{org.website}</a>}
                              {org.email && <a href={`mailto:${org.email}`} className="text-[10px] text-teal-600 hover:underline truncate max-w-[120px]">{org.email}</a>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Durum</label>
              <select value={form.status} onChange={e => handleChange('status', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="published">Yayında</option>
                <option value="draft">Taslak</option>
                <option value="archived">Arşiv</option>
              </select>
            </div>
            <div className="md:col-span-2 flex items-center gap-3">
              <input type="checkbox" id="is_featured" checked={form.is_featured}
                onChange={e => handleChange('is_featured', e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded" />
              <label htmlFor="is_featured" className="text-sm font-medium text-gray-700">
                Öne Çıkan Fuar (Ana sayfada ve şehir sayfalarında gösterilir)
              </label>
            </div>
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
            İptal
          </button>
          <button data-testid="fair-save-btn" onClick={handleSubmit} disabled={saving || !form.name || !form.city}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

function AiGenerateModal({ onClose, onGenerated }) {
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleGenerate = async () => {
    if (!city) return;
    setLoading(true);
    try {
      const token = getToken();
      const res = await fetch(`${API}/ultra/fairs/generate-ai`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ city, country }),
      });
      if (!res.ok) throw new Error('AI oluşturma başarısız');
      const data = await res.json();
      setResult(data);
    } catch (e) {
      alert('Hata: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="p-6 border-b flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-600 rounded-xl flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">AI ile Fuar Oluştur</h2>
              <p className="text-sm text-gray-500">Seçilen şehir için 5 fuar otomatik oluşturulur</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          {!result ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Şehir *</label>
                <input data-testid="ai-city-input" value={city} onChange={e => setCity(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="örn. Tokyo, Singapore, Vienna..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ülke</label>
                <input value={country} onChange={e => setCountry(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="örn. Japonya, Singapur..." />
              </div>
              <div className="bg-purple-50 rounded-xl p-4 text-sm text-purple-700">
                <strong>Not:</strong> AI tarafından oluşturulan fuarlar <strong>taslak</strong> olarak kaydedilir. Kontrol ettikten sonra yayına alabilirsiniz.
              </div>
              <button data-testid="ai-generate-btn" onClick={handleGenerate} disabled={loading || !city}
                className="w-full py-3 text-sm font-semibold text-white bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2">
                {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {loading ? `${city} için AI Üretiyor...` : 'AI ile Fuar Oluştur'}
              </button>
            </>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-green-600 font-semibold">
                <CheckCircle className="w-5 h-5" />
                {result.created} fuar taslak olarak oluşturuldu!
              </div>
              {result.fairs?.map((f, i) => (
                <div key={i} className="bg-gray-50 rounded-lg p-3 text-sm">
                  <p className="font-medium text-gray-900">{f.name}</p>
                  <p className="text-gray-500">{f.venue} • {f.start_date}</p>
                </div>
              ))}
              <button onClick={() => { onGenerated(); onClose(); }}
                className="w-full py-2 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700">
                Listeye Dön
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}

function WebScrapeModal({ onClose }) {
  const [status, setStatus] = useState(null);
  const [progress, setProgress] = useState(null);

  const poll = useCallback(async () => {
    try {
      const token = getToken();
      const res = await fetch(`${API}/ultra/fairs/scrape-websites-progress`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return;
      const data = await res.json();
      setProgress(data);
      if (data.running) {
        setStatus('running');
      } else if (!data.running && status === 'running') setStatus('done');
    } catch { /* silent */ }
  }, [status]);

  useEffect(() => { poll(); }, []);

  useEffect(() => {
    if (status === 'running') {
      const id = setInterval(poll, 3000);
      return () => clearInterval(id);
    }
  }, [status, poll]);

  const handleStart = async (reset = false) => {
    setStatus('running');
    const token = getToken();
    await fetch(`${API}/ultra/fairs/scrape-websites`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ reset }),
    });
    poll();
  };

  const handleStop = async () => {
    const token = getToken();
    await fetch(`${API}/ultra/fairs/scrape-websites-stop`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` }
    });
    setStatus('done');
  };

  const isRunning = status === 'running' || progress?.running;
  const pct = progress?.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="p-6 border-b flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-700 rounded-xl flex items-center justify-center">
              <ScanLine className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Website Tarih Güncelleyici</h2>
              <p className="text-xs text-gray-500">Her fuarın resmi sitesinden güncel tarihleri çeker</p>
            </div>
          </div>
          {!isRunning && <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full"><X className="w-5 h-5" /></button>}
        </div>

        <div className="p-6 space-y-5">
          {/* Veritabanı istatistikleri */}
          {progress && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: 'Resmi Site', value: progress.db_with_official?.toLocaleString() || '0', color: 'teal', icon: Globe, tooltip: 'Kontak zenginleştirmeden gelen doğru fuar siteleri' },
                  { label: 'Genel Website', value: progress.db_with_website?.toLocaleString() || '—', color: 'blue', icon: Globe, tooltip: 'Website alanı dolu olan fuarlar' },
                  { label: 'Tarih Güncellendi', value: progress.db_updated?.toLocaleString() || '0', color: 'emerald', icon: CheckCheck },
                ].map(s => (
                  <div key={s.label} title={s.tooltip || ''} className={`bg-${s.color}-50 border border-${s.color}-100 rounded-xl p-3 text-center`}>
                    <s.icon className={`w-4 h-4 text-${s.color}-500 mx-auto mb-1`} />
                    <p className={`text-base font-bold text-${s.color}-700`}>{s.value}</p>
                    <p className={`text-[10px] text-${s.color}-500`}>{s.label}</p>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: 'Tarih Aynı', value: ((progress.db_success || 0) - (progress.db_updated || 0)).toLocaleString(), color: 'gray', icon: Minus },
                  { label: 'Tarih Yok', value: progress.db_no_dates?.toLocaleString() || '0', color: 'yellow', icon: AlertCircle, tooltip: 'Siteye ulaşıldı ama tarih formatı bulunamadı' },
                  { label: 'Erişilemedi', value: progress.db_failed?.toLocaleString() || '0', color: 'red', icon: AlertCircle, tooltip: 'Site yanıt vermedi / kapalı / bot engeli' },
                ].map(s => (
                  <div key={s.label} title={s.tooltip || ''} className={`bg-${s.color}-50 border border-${s.color}-100 rounded-xl p-3 text-center`}>
                    <s.icon className={`w-4 h-4 text-${s.color}-500 mx-auto mb-1`} />
                    <p className={`text-base font-bold text-${s.color}-700`}>{s.value}</p>
                    <p className={`text-[10px] text-${s.color}-500`}>{s.label}</p>
                  </div>
                ))}
              </div>
              {/* Açıklama notu */}
              <div className="bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 text-xs text-amber-800">
                <span className="font-semibold">ℹ️ Normal durum:</span> Binlerce uluslararası fuar sitesinde yüksek başarısızlık oranı beklenir — siteler geçici kapalı olabilir, bot engelleyebilir veya tarih formatı tanınamıyor olabilir. "Resmi Site" alanı doluysa tarih bulma oranı artar.
              </div>
            </div>
          )}

          {/* Çalışıyor */}
          {isRunning && (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-gray-700 flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin text-violet-600" /> Taranıyor...
                </span>
                <span className="font-bold text-violet-700">{pct}%</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2.5">
                <div className="bg-violet-500 h-2.5 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
              </div>
              <div className="grid grid-cols-4 gap-2 text-xs text-center">
                <div className="bg-gray-50 rounded-lg p-2">
                  <p className="font-bold text-gray-700">{progress?.done?.toLocaleString()} / {progress?.total?.toLocaleString()}</p>
                  <p className="text-gray-400">İşlenen</p>
                </div>
                <div className="bg-emerald-50 rounded-lg p-2">
                  <p className="font-bold text-emerald-700">{progress?.updated || 0}</p>
                  <p className="text-emerald-500">Tarih Güncellendi</p>
                </div>
                <div className="bg-yellow-50 rounded-lg p-2">
                  <p className="font-bold text-yellow-700">{progress?.no_dates || 0}</p>
                  <p className="text-yellow-500">Tarih Yok</p>
                </div>
                <div className="bg-red-50 rounded-lg p-2">
                  <p className="font-bold text-red-700">{progress?.failed || 0}</p>
                  <p className="text-red-400">Erişilemedi</p>
                </div>
              </div>
              <button onClick={handleStop}
                className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-red-600 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100">
                <StopCircle className="w-4 h-4" /> Durdur
              </button>
            </div>
          )}

          {/* Tamamlandı */}
          {status === 'done' && !isRunning && (
            <div className="bg-emerald-50 rounded-xl p-4 text-center">
              <CheckCircle className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
              <p className="font-semibold text-emerald-800">Tamamlandı!</p>
              <p className="text-xs text-emerald-600 mt-1">
                {progress?.updated || 0} tarih güncellendi · {progress?.unchanged || 0} tarih aynıydı · {progress?.no_dates || 0} tarih yok · {progress?.failed || 0} erişilemedi
              </p>
            </div>
          )}

          {/* Başlatma */}
          {!isRunning && status !== 'done' && (
            <div className="space-y-3">
              <div className="bg-violet-50 rounded-xl p-4 text-sm text-violet-800">
                <p className="font-bold mb-2 flex items-center gap-2"><ScanLine className="w-4 h-4" /> Ne yapar?</p>
                <ul className="space-y-1 text-xs text-violet-700 list-disc list-inside">
                  <li>Her fuarın resmi websitesini ziyaret eder</li>
                  <li>JSON-LD, meta tags ve metin içinden tarihleri bulur</li>
                  <li>Yeni tarih bulunursa veritabanını günceller</li>
                  <li>Listede <strong>"✓ Tarih Güncellendi [tarih]"</strong> rozeti görünür</li>
                  <li>Arka planda çalışır, sayfayı kapatabilirsiniz</li>
                </ul>
              </div>
              <button onClick={() => handleStart(false)}
                className="w-full flex items-center justify-center gap-2 py-3 text-sm font-bold text-white bg-gradient-to-r from-violet-600 to-purple-700 rounded-xl hover:opacity-90">
                <ScanLine className="w-4 h-4" />
                {progress?.db_scraped > 0
                  ? `Kalan ${((progress.db_with_website || 0) - (progress.db_scraped || 0)).toLocaleString()} Fuarı Tara`
                  : `${progress?.db_with_website?.toLocaleString() || '?'} Fuarı Tara`
                }
              </button>
              {progress?.db_scraped > 0 && (
                <button onClick={() => handleStart(true)}
                  className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-violet-700 bg-violet-50 border border-violet-200 rounded-xl hover:bg-violet-100">
                  <RefreshCw className="w-4 h-4" /> Tümünü Sıfırlayıp Tekrar Tara
                </button>
              )}
            </div>
          )}

          {status === 'done' && (
            <button onClick={onClose}
              className="w-full py-2.5 text-sm font-semibold text-white bg-violet-600 rounded-xl hover:bg-violet-700">
              Listeye Dön
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
}


function WebsiteEnrichModal({ onClose }) {
  const [status, setStatus] = useState(null); // null | 'running' | 'done'
  const [progress, setProgress] = useState(null);

  const poll = useCallback(async () => {
    try {
      const token = getToken();
      const res = await fetch(`${API}/ultra/fairs/enrich-websites-progress`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return;
      const data = await res.json();
      setProgress(data);
      if (data.running) {
        setStatus('running');
      } else if (!data.running && status === 'running') setStatus('done');
    } catch { /* silent */ }
  }, [status]);

  useEffect(() => { poll(); }, []);

  useEffect(() => {
    if (status === 'running') {
      const id = setInterval(poll, 2500);
      return () => clearInterval(id);
    }
  }, [status, poll]);

  const handleStart = async (resetFailed = false) => {
    setStatus('running');
    const token = getToken();
    await fetch(`${API}/ultra/fairs/enrich-websites`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ reset_failed: resetFailed }),
    });
    poll();
  };

  const handleStop = async () => {
    const token = getToken();
    await fetch(`${API}/ultra/fairs/enrich-websites-stop`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` }
    });
    setStatus('done');
  };

  const pct = progress && progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;
  const isRunning = status === 'running' || progress?.running;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="p-6 border-b flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
              <Link2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Website Zenginleştirme</h2>
              <p className="text-xs text-gray-500">EventsEye detay sayfalarından resmi website URL'lerini çeker</p>
            </div>
          </div>
          {!isRunning && <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full"><X className="w-5 h-5" /></button>}
        </div>

        <div className="p-6 space-y-5">
          {/* Stats */}
          {progress && (
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'Website Var', value: progress.total_with_website?.toLocaleString() || '—', color: 'emerald' },
                { label: 'Bulunamadı', value: progress.total_failed?.toLocaleString() || '0', color: 'red' },
                { label: 'Bekliyor', value: progress.total > 0 ? progress.total.toLocaleString() : '—', color: 'blue' },
              ].map(s => (
                <div key={s.label} className={`bg-${s.color}-50 border border-${s.color}-100 rounded-xl p-3 text-center`}>
                  <p className={`text-lg font-bold text-${s.color}-700`}>{s.value}</p>
                  <p className={`text-xs text-${s.color}-500`}>{s.label}</p>
                </div>
              ))}
            </div>
          )}

          {/* Progress Bar (running) */}
          {isRunning && (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-gray-700 flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin text-blue-600" /> Çekiliyor...
                </span>
                <span className="font-bold text-blue-700">{pct}%</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2.5">
                <div className="bg-blue-500 h-2.5 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
              </div>
              <div className="flex justify-between text-xs text-gray-500">
                <span>{progress?.done?.toLocaleString()} / {progress?.total?.toLocaleString()} fuar</span>
                <span className="text-emerald-600 font-semibold">✓ {progress?.updated} bulundu</span>
              </div>
              <button onClick={handleStop}
                className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-red-600 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100">
                <StopCircle className="w-4 h-4" /> Durdur
              </button>
            </div>
          )}

          {/* Done */}
          {status === 'done' && !isRunning && (
            <div className="bg-emerald-50 rounded-xl p-4 text-center">
              <CheckCircle className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
              <p className="font-semibold text-emerald-800 text-sm">Tamamlandı!</p>
              <p className="text-xs text-emerald-600 mt-1">{progress?.updated} website eklendi, {progress?.failed} bulunamadı</p>
            </div>
          )}

          {/* Start buttons */}
          {!isRunning && status !== 'done' && (
            <div className="space-y-3">
              <div className="bg-blue-50 rounded-xl p-4 text-sm text-blue-700">
                <p className="font-semibold mb-1">Ne yapar?</p>
                <ul className="space-y-1 text-xs list-disc list-inside">
                  <li>Website URL'si boş olan tüm fuarların EventsEye sayfasını ziyaret eder</li>
                  <li>Resmi website linkini bulursa kaydeder</li>
                  <li>Bulamazsa <strong>"Web Yok"</strong> rozeti işaretler</li>
                  <li>Sayfayı kapatabilirsiniz, arka planda devam eder</li>
                </ul>
              </div>
              <button onClick={() => handleStart(false)}
                className="w-full flex items-center justify-center gap-2 py-3 text-sm font-bold text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl hover:opacity-90">
                <Link2 className="w-4 h-4" /> Website Zenginleştirmeyi Başlat
              </button>
              {progress?.total_failed > 0 && (
                <button onClick={() => handleStart(true)}
                  className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-orange-700 bg-orange-50 border border-orange-200 rounded-xl hover:bg-orange-100">
                  <RefreshCw className="w-4 h-4" /> "Web Yok" işaretlileri de tekrar dene ({progress.total_failed})
                </button>
              )}
            </div>
          )}

          {status === 'done' && (
            <button onClick={onClose}
              className="w-full py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700">
              Listeye Dön
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
}



function ContactEnrichModal({ onClose, onMinimize }) {
  const [status, setStatus] = useState(null);
  const [progress, setProgress] = useState(null);

  const poll = useCallback(async () => {
    try {
      const token = getToken();
      const res = await fetch(`${API}/ultra/fairs/enrich-contacts-progress`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return;
      const data = await res.json();
      setProgress(data);
      // Arka planda çalışıyorsa otomatik algıla
      if (data.running) {
        setStatus('running');
      } else if (!data.running && status === 'running') {
        setStatus('done');
      }
    } catch { /* silent */ }
  }, [status]);

  useEffect(() => { poll(); }, []);
  useEffect(() => {
    if (status === 'running') {
      const id = setInterval(poll, 2500);
      return () => clearInterval(id);
    }
  }, [status, poll]);

  const handleStart = async (reset = false) => {
    setStatus('running');
    const token = getToken();
    await fetch(`${API}/ultra/fairs/enrich-contacts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ reset }),
    });
    poll();
  };

  const handleStop = async () => {
    const token = getToken();
    await fetch(`${API}/ultra/fairs/enrich-contacts-stop`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}` }
    });
    setStatus('done');
  };

  const pct = progress && progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;
  const isRunning = status === 'running' || progress?.running;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) { onMinimize ? onMinimize() : onClose(); } }}
    >
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="p-6 border-b flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-xl flex items-center justify-center">
              <Mail className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Kontak Bilgisi Zenginleştirme</h2>
              <p className="text-xs text-gray-500">EventsEye'dan venue, organizatör ve resmi iletişim bilgilerini çeker</p>
            </div>
          </div>
          <button onClick={onMinimize || onClose} className="p-2 hover:bg-gray-100 rounded-full" title="Küçült">
            <Minus className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {progress && (
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Zenginleştirildi', value: progress.total_enriched?.toLocaleString() || '0', color: 'emerald' },
                { label: 'Resmi Website', value: progress.total_with_official_site?.toLocaleString() || '0', color: 'blue' },
                { label: 'Event E-mail', value: progress.total_with_email?.toLocaleString() || '0', color: 'violet' },
                { label: 'Organizatör Verisi', value: progress.total_with_organizers?.toLocaleString() || '0', color: 'orange' },
              ].map(s => (
                <div key={s.label} className={`bg-${s.color}-50 border border-${s.color}-100 rounded-xl p-3 text-center`}>
                  <p className={`text-lg font-bold text-${s.color}-700`}>{s.value}</p>
                  <p className={`text-xs text-${s.color}-500`}>{s.label}</p>
                </div>
              ))}
            </div>
          )}

          {isRunning && (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-gray-700 flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin text-teal-600" /> Kontak bilgileri çekiliyor...
                </span>
                <span className="font-bold text-teal-700">{pct}%</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2.5">
                <div className="bg-teal-500 h-2.5 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
              </div>
              <div className="flex justify-between text-xs text-gray-500">
                <span>{progress?.done?.toLocaleString()} / {progress?.total?.toLocaleString()} fuar</span>
                <span className="text-emerald-600 font-semibold">✓ {progress?.updated} güncellendi</span>
              </div>
              <button onClick={handleStop}
                className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-red-600 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100">
                <StopCircle className="w-4 h-4" /> Durdur
              </button>
            </div>
          )}

          {status === 'done' && !isRunning && (
            <div className="bg-emerald-50 rounded-xl p-4 text-center">
              <CheckCircle className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
              <p className="font-semibold text-emerald-800 text-sm">Tamamlandı!</p>
              <p className="text-xs text-emerald-600 mt-1">{progress?.updated} fuar kontak bilgisiyle güncellendi</p>
            </div>
          )}

          {!isRunning && status !== 'done' && (
            <div className="space-y-3">
              <div className="bg-teal-50 rounded-xl p-4 text-sm text-teal-800 space-y-2">
                <p className="font-semibold mb-1">Ne çeker? (EventsEye detay sayfasından)</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="flex items-start gap-1.5"><Globe className="w-3.5 h-3.5 text-teal-600 mt-0.5 flex-shrink-0" /><span><b>Resmi Website</b> — Contact Info bölümündeki "Official Web Site"</span></div>
                  <div className="flex items-start gap-1.5"><Mail className="w-3.5 h-3.5 text-teal-600 mt-0.5 flex-shrink-0" /><span><b>Event E-mail</b> — "Event's E-mail" adresi</span></div>
                  <div className="flex items-start gap-1.5"><Building2 className="w-3.5 h-3.5 text-teal-600 mt-0.5 flex-shrink-0" /><span><b>Venue Website & E-mail</b> — Mekan iletişim bilgileri</span></div>
                  <div className="flex items-start gap-1.5"><Users className="w-3.5 h-3.5 text-teal-600 mt-0.5 flex-shrink-0" /><span><b>Organizatörler</b> — Ad, website, e-posta listesi</span></div>
                </div>
              </div>
              <button onClick={() => handleStart(false)}
                className="w-full flex items-center justify-center gap-2 py-3 text-sm font-bold text-white bg-gradient-to-r from-teal-600 to-emerald-600 rounded-xl hover:opacity-90">
                <Mail className="w-4 h-4" /> Kontak Bilgisi Zenginleştirmeyi Başlat
              </button>
              {progress?.total_enriched > 0 && (
                <button onClick={() => handleStart(true)}
                  className="w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-orange-700 bg-orange-50 border border-orange-200 rounded-xl hover:bg-orange-100">
                  <RefreshCw className="w-4 h-4" /> Tümünü Sıfırla ve Yeniden Çek
                </button>
              )}
            </div>
          )}

          {status === 'done' && (
            <button onClick={onClose}
              className="w-full py-2.5 text-sm font-semibold text-white bg-teal-600 rounded-xl hover:bg-teal-700">
              Listeye Dön
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
}



function EventsEyeModal({ onClose, onComplete }) {
  const [job, setJob] = useState(null);
  const [yearOffsets, setYearOffsets] = useState([0, 1]);

  const pollStatus = useCallback(async () => {
    try {
      const token = getToken();
      const res = await fetch(`${API}/ultra/fairs/scrape-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return;
      const data = await res.json();
      setJob(data);
      if (data.status === 'done') { setStatus('done'); onComplete(); }
      else if (data.status === 'running' || data.status === 'queued') setStatus('running');
    } catch { /* silent */ }
  }, [onComplete]);

  useEffect(() => {
    pollStatus(); // check immediately on mount
  }, [pollStatus]);

  useEffect(() => {
    if (status === 'running') {
      const id = setInterval(pollStatus, 3000);
      return () => clearInterval(id);
    }
  }, [status, pollStatus]);

  const handleStart = async () => {
    setStatus('starting');
    try {
      const token = getToken();
      const res = await fetch(`${API}/ultra/fairs/scrape-eventseye`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ year_offsets: yearOffsets }),
      });
      const data = await res.json();
      setJob(data);
      setStatus('running');
    } catch (e) {
      setStatus('error');
    }
  };

  const progressPct = job?.progress || 0;
  const isRunning = status === 'running' || status === 'starting';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="p-6 border-b flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
              <Database className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">EventsEye İçeri Aktar</h2>
              <p className="text-xs text-gray-500">11,000+ uluslararası fuar verisi</p>
            </div>
          </div>
          {!isRunning && <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full"><X className="w-5 h-5" /></button>}
        </div>

        <div className="p-6 space-y-5">
          {/* Status: never run or idle */}
          {(status === null || status === 'done') && job?.status !== 'running' && (
            <>
              {job?.total_eventseye_fairs > 0 && (
                <div className="bg-emerald-50 rounded-xl p-4 flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-emerald-800">Veritabanınızda {job.total_eventseye_fairs.toLocaleString()} EventsEye fuarı var</p>
                    <p className="text-xs text-emerald-600 mt-0.5">Tekrar çalıştırırsanız sadece yeni fuarlar eklenir.</p>
                  </div>
                </div>
              )}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-3">Hangi yılları çekelim?</p>
                {[{label: '2026', val: 0}, {label: '2027', val: 1}, {label: '2028', val: 2}].map(y => (
                  <label key={y.val} className="flex items-center gap-2 mb-2 cursor-pointer">
                    <input type="checkbox" checked={yearOffsets.includes(y.val)}
                      onChange={e => setYearOffsets(prev => e.target.checked ? [...prev, y.val] : prev.filter(v => v !== y.val))}
                      className="w-4 h-4 text-emerald-600 rounded" />
                    <span className="text-sm text-gray-700">{y.label} ({y.label === '2026' ? '~4,500' : y.label === '2027' ? '~4,200' : '~3,200'} fuar tahmini)</span>
                  </label>
                ))}
              </div>
              <div className="bg-amber-50 rounded-xl p-4 text-sm text-amber-700">
                <strong>Süre tahmini:</strong> Seçilen yıl sayısına göre 3–10 dakika. Sayfayı kapatabilirsiniz, arka planda devam eder.
              </div>
              <button data-testid="start-scrape-btn" onClick={handleStart} disabled={yearOffsets.length === 0}
                className="w-full py-3 text-sm font-bold text-white bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl hover:opacity-90 disabled:opacity-40 flex items-center justify-center gap-2">
                <Zap className="w-4 h-4" />
                EventsEye'den {yearOffsets.length > 0 ? `${yearOffsets.length} Yıl` : ''} Çek
              </button>
            </>
          )}

          {/* Status: running */}
          {isRunning && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <RefreshCw className="w-5 h-5 text-emerald-600 animate-spin" />
                <div>
                  <p className="text-sm font-semibold text-gray-800">Scraping devam ediyor...</p>
                  {job?.current_month && (
                    <p className="text-xs text-gray-500">Şu an: {job.current_month}</p>
                  )}
                </div>
              </div>

              {/* Progress bar */}
              <div className="space-y-2">
                <div className="flex justify-between text-xs text-gray-500">
                  <span>{progressPct}% tamamlandı</span>
                  <span>{job?.inserted?.toLocaleString() || 0} fuar eklendi</span>
                </div>
                <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${progressPct}%` }}
                    transition={{ duration: 0.5 }}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Eklendi', val: job?.inserted || 0, color: 'text-emerald-600' },
                  { label: 'Atlandı', val: job?.skipped || 0, color: 'text-gray-500' },
                  { label: 'Hata', val: job?.errors || 0, color: 'text-red-500' },
                ].map(s => (
                  <div key={s.label} className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className={`text-xl font-bold ${s.color}`}>{s.val.toLocaleString()}</p>
                    <p className="text-xs text-gray-400">{s.label}</p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 text-center">Sayfayı kapatabilirsiniz, arka planda devam eder.</p>
            </div>
          )}

          {/* Status: done */}
          {status === 'done' && (
            <div className="space-y-4 text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <div>
                <p className="text-lg font-bold text-gray-900">Tamamlandı!</p>
                <p className="text-sm text-gray-500 mt-1">
                  <strong className="text-emerald-600">{job?.inserted?.toLocaleString()}</strong> yeni fuar eklendi,
                  {' '}<strong>{job?.skipped?.toLocaleString()}</strong> atlandı (zaten vardı)
                </p>
              </div>
              <button onClick={onClose}
                className="w-full py-2 text-sm font-semibold text-white bg-emerald-600 rounded-xl hover:bg-emerald-700">
                Listeye Dön
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}

export default function UltraFairs() {
  const auth = useAuth();
  const [fairs, setFairs] = useState([]);
  const [stats, setStats] = useState({ total_fairs: 0, published: 0, cities_covered: 0, this_month: 0 });
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Tümü');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [editFair, setEditFair] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  const [showEventseye, setShowEventseye] = useState(false);
  const [showEnrich, setShowEnrich] = useState(false);
  const [showWebScrape, setShowWebScrape] = useState(false);
  const [showContactEnrich, setShowContactEnrich] = useState(false);
  const [contactProgress, setContactProgress] = useState(null);

  // Kontak zenginleştirme durumunu periyodik olarak izle (modal kapalıyken)
  useEffect(() => {
    let id;
    const checkProgress = async () => {
      try {
        const token = getToken();
        if (!token) return;
        const res = await fetch(`${API}/ultra/fairs/enrich-contacts-progress`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) return;
        const data = await res.json();
        setContactProgress(data);
      } catch { /* silent */ }
    };
    checkProgress();
    id = setInterval(checkProgress, 4000);
    return () => clearInterval(id);
  }, []);
  const [classifyStatus, setClassifyStatus] = useState(null);
  const [dateUpdateRunning, setDateUpdateRunning] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [page, setPage] = useState(1);
  const PER_PAGE = 15;

  const fetchFairs = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const token = getToken();
      if (!token) { setFetchError('Oturum bulunamadı — lütfen tekrar giriş yapın.'); return; }
      const params = new URLSearchParams({ limit: 200 });
      if (search) params.set('search', search);
      if (selectedCategory !== 'Tümü') params.set('category', selectedCategory);
      if (selectedStatus) params.set('status', selectedStatus);
      const res = await fetch(`${API}/ultra/fairs?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.status === 401 || res.status === 403) {
        // Token süresi dolmuş olabilir — yenilemeyi dene
        if (auth?.refreshTokenIfExists) {
          await auth.refreshTokenIfExists();
          const newToken = getToken();
          if (newToken && newToken !== token) {
            // Token yenilendi, tekrar dene
            const retryRes = await fetch(`${API}/ultra/fairs?${params}`, {
              headers: { Authorization: `Bearer ${newToken}` }
            });
            if (retryRes.ok) {
              const retryData = await retryRes.json();
              setFairs(retryData.items || []);
              if (retryData.stats) setStats(retryData.stats);
              return;
            }
          }
        }
        setFetchError('Oturum süresi dolmuş — çıkış yapıp tekrar giriş yapın.');
        return;
      }
      if (!res.ok) { setFetchError(`Sunucu hatası (${res.status}) — sayfayı yenileyin.`); return; }
      const data = await res.json();
      setFairs(data.items || []);
      if (data.stats) setStats(data.stats);
    } catch (e) {
      setFetchError('Bağlantı hatası — internet bağlantınızı kontrol edin veya sayfayı yenileyin.');
    } finally {
      setLoading(false);
    }
  }, [search, selectedCategory, selectedStatus]);

  useEffect(() => { fetchFairs(); }, [fetchFairs]);
  useEffect(() => { setPage(1); }, [search, selectedCategory, selectedStatus]);

  const handleSave = (saved, isEdit) => {
    if (isEdit) setFairs(p => p.map(f => f.fair_id === saved.fair_id ? saved : f));
    else { setFairs(p => [saved, ...p]); fetchFairs(); }
  };

  const handleDelete = async (id) => {
    try {
      const token = getToken();
      await fetch(`${API}/ultra/fairs/${id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
      });
      setFairs(p => p.filter(f => f.fair_id !== id));
      setDeleteId(null);
    } catch { alert('Silinemedi'); }
  };

  const handleToggleFeatured = async (id) => {
    try {
      const token = getToken();
      const res = await fetch(`${API}/ultra/fairs/${id}/toggle-featured`, {
        method: 'PATCH', headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setFairs(p => p.map(f => f.fair_id === id ? { ...f, is_featured: data.is_featured } : f));
    } catch { /* silent */ }
  };

  const handleToggleStatus = async (fair) => {
    const newStatus = fair.status === 'published' ? 'draft' : 'published';
    try {
      const token = getToken();
      const res = await fetch(`${API}/ultra/fairs/${fair.fair_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus })
      });
      const data = await res.json();
      setFairs(p => p.map(f => f.fair_id === fair.fair_id ? { ...f, status: data.status } : f));
    } catch { /* silent */ }
  };

  const handleClassify = async () => {
    try {
      const token = getToken();
      setClassifyStatus('running');
      const res = await fetch(`${API}/ultra/fairs/classify-categories`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      const d = await res.json();
      if (d.status === 'nothing_to_do') { setClassifyStatus('done'); return; }
      // Poll for completion
      const poll = setInterval(async () => {
        const r2 = await fetch(`${API}/ultra/fairs/classify-status`, { headers: { Authorization: `Bearer ${token}` } });
        const d2 = await r2.json();
        if (d2.status === 'done') {
          clearInterval(poll);
          setClassifyStatus('done');
          fetchFairs();
        }
      }, 2000);
    } catch { setClassifyStatus(null); }
  };

  const handleUpdateDates = async () => {
    try {
      const token = getToken();
      setDateUpdateRunning(true);
      await fetch(`${API}/ultra/fairs/update-dates`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      // Poll for completion
      const poll = setInterval(async () => {
        const r = await fetch(`${API}/ultra/fairs/update-dates-progress`, { headers: { Authorization: `Bearer ${token}` } });
        const d = await r.json();
        if (!d.running) {
          clearInterval(poll);
          setDateUpdateRunning(false);
          fetchFairs();
        }
      }, 3000);
    } catch { setDateUpdateRunning(false); }
  };

  const exportCSV = () => {
    const header = ['Ad', 'Şehir', 'Ülke', 'Venue', 'Başlangıç', 'Bitiş', 'Kategori', 'Ziyaretçi', 'Sıklık', 'Durum'];
    const rows = fairs.map(f => [f.name, f.city, f.country, f.venue, f.start_date, f.end_date, f.category, f.visitors, f.frequency, f.status]);
    const csv = [header, ...rows].map(r => r.map(v => `"${v || ''}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `fuarlar_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const paged = fairs.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const totalPages = Math.ceil(fairs.length / PER_PAGE);

  return (
    <div className="space-y-6" data-testid="ultra-fairs-page">
      {/* Hata Mesajı */}
      {fetchError && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold text-red-800 text-sm">Fuar verileri yüklenemedi</p>
            <p className="text-xs text-red-600 mt-0.5">{fetchError}</p>
          </div>
          <button onClick={fetchFairs} className="shrink-0 text-xs font-semibold text-red-700 bg-red-100 hover:bg-red-200 px-3 py-1.5 rounded-lg flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> Tekrar Dene
          </button>
        </div>
      )}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Fuar Veritabanı</h1>
          <p className="text-sm text-gray-500 mt-1">Uluslararası ticaret fuarları ve etkinlik takvimi</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={exportCSV} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
            <Download className="w-4 h-4" />
            Excel İndir
          </button>
          <button data-testid="classify-categories-btn"
            onClick={handleClassify} disabled={classifyStatus === 'running'}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-60">
            {classifyStatus === 'running'
              ? <><RefreshCw className="w-4 h-4 animate-spin" />Sınıflandırıyor...</>
              : classifyStatus === 'done'
              ? <><CheckCircle className="w-4 h-4 text-green-600" />Kategoriler Güncellendi</>
              : <><Zap className="w-4 h-4" />Kategorileri Güncelle</>
            }
          </button>
          <button data-testid="update-dates-btn"
            onClick={handleUpdateDates} disabled={dateUpdateRunning}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 disabled:opacity-60">
            {dateUpdateRunning
              ? <><RefreshCw className="w-4 h-4 animate-spin" />Tarihler Güncelleniyor...</>
              : <><Calendar className="w-4 h-4" />Tarihleri Güncelle</>
            }
          </button>
          <button data-testid="eventseye-import-btn" onClick={() => setShowEventseye(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100">
            <Database className="w-4 h-4" />
            EventsEye İçeri Aktar
          </button>
          <button data-testid="enrich-websites-btn" onClick={() => setShowEnrich(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100">
            <Link2 className="w-4 h-4" />
            Website Zenginleştir
          </button>
          <button data-testid="enrich-contacts-btn" onClick={() => setShowContactEnrich(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-teal-700 bg-teal-50 border border-teal-200 rounded-lg hover:bg-teal-100">
            <Mail className="w-4 h-4" />
            Kontak Zenginleştir
          </button>
          <button data-testid="web-scrape-btn" onClick={() => setShowWebScrape(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-violet-700 bg-violet-50 border border-violet-200 rounded-lg hover:bg-violet-100">
            <ScanLine className="w-4 h-4" />
            Tarih Güncelle
          </button>
          <button data-testid="ai-generate-fair-btn" onClick={() => setShowAiModal(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100">
            <Sparkles className="w-4 h-4" />
            AI ile Oluştur
          </button>
          <button data-testid="add-fair-btn" onClick={() => { setEditFair(null); setShowModal(true); }}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700">
            <Plus className="w-4 h-4" />
            Fuar Ekle
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Toplam Fuar', value: stats.total_fairs, icon: Calendar, color: 'blue' },
          { label: 'Yayında', value: stats.published, icon: CheckCircle, color: 'green' },
          { label: 'Kapsanan Şehir', value: stats.cities_covered, icon: MapPin, color: 'purple' },
          { label: 'Bu Ay', value: stats.this_month, icon: TrendingUp, color: 'orange' },
        ].map((kpi, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
            className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-500">{kpi.label}</span>
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center bg-${kpi.color}-50`}>
                <kpi.icon className={`w-5 h-5 text-${kpi.color}-600`} />
              </div>
            </div>
            <p className="text-3xl font-bold text-gray-900">{loading ? '—' : kpi.value}</p>
          </motion.div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input data-testid="fairs-search" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Fuar adı, şehir veya ülke ara..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 focus:bg-white" />
          </div>
          <select data-testid="fairs-category-filter" value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-600 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[160px]">
            {CATEGORIES.map(c => <option key={c}>{c}</option>)}
          </select>
          <select data-testid="fairs-status-filter" value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-600 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[140px]">
            <option value="">Tüm Durumlar</option>
            <option value="published">Yayında</option>
            <option value="draft">Taslak</option>
            <option value="archived">Arşiv</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-gray-800">Fuar Listesi</h3>
            <span className="bg-blue-50 text-blue-700 text-xs font-medium px-2 py-0.5 rounded-full">{fairs.length} fuar</span>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
          </div>
        ) : fairs.length === 0 ? (
          <div className="text-center py-20">
            <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">Fuar bulunamadı</p>
            <button onClick={() => { setEditFair(null); setShowModal(true); }}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
              İlk Fuarı Ekle
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="fairs-table">
                <thead>
                  <tr className="border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50">
                    <th className="text-left px-6 py-3">Fuar Adı</th>
                    <th className="text-left px-4 py-3">Şehir / Venue</th>
                    <th className="text-left px-4 py-3">Tarihler</th>
                    <th className="text-left px-4 py-3">Kategori</th>
                    <th className="text-left px-4 py-3">Ziyaretçi</th>
                    <th className="text-center px-4 py-3">Durum</th>
                    <th className="text-center px-4 py-3">Öne Çıkan</th>
                    <th className="text-center px-4 py-3">İşlem</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  <AnimatePresence>
                    {paged.map((fair, i) => (
                      <motion.tr key={fair.fair_id}
                        initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.03 }}
                        className="hover:bg-gray-50 transition-colors group">
                        <td className="px-6 py-3">
                          <div className="font-medium text-gray-900 max-w-[220px] leading-tight">{fair.name}</div>
                          <div className="text-xs text-gray-400 mt-0.5">{fair.frequency}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 text-gray-700">
                            <MapPin className="w-3 h-3 text-gray-400 flex-shrink-0" />
                            <span>{fair.city}</span>
                            {fair.country && <span className="text-gray-400">, {fair.country}</span>}
                          </div>
                          {fair.venue && <div className="text-xs text-gray-400 mt-0.5 truncate max-w-[180px]">{fair.venue}</div>}
                        </td>
                        <td className="px-4 py-3 text-gray-600 text-xs">
                          <div>{fair.start_date || '—'}</div>
                          <div className="text-gray-400">{fair.end_date || ''}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${CATEGORY_COLORS[fair.category] || 'bg-gray-100 text-gray-600'}`}>
                            {fair.category}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {fair.visitors && (
                            <div className="flex items-center gap-1 text-gray-700">
                              <Users className="w-3 h-3 text-gray-400" />
                              <span className="text-xs">{fair.visitors}</span>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button onClick={() => handleToggleStatus(fair)}
                            className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity ${STATUS_COLORS[fair.status] || STATUS_COLORS.draft}`}>
                            {fair.status === 'published' ? 'Yayında' : fair.status === 'draft' ? 'Taslak' : 'Arşiv'}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button onClick={() => handleToggleFeatured(fair.fair_id)}
                            className="p-1.5 rounded-lg hover:bg-yellow-50 transition-colors">
                            {fair.is_featured
                              ? <Star className="w-4 h-4 text-yellow-500 fill-yellow-400" />
                              : <StarOff className="w-4 h-4 text-gray-300 group-hover:text-gray-400" />}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col items-center gap-1">
                            {/* Website link veya durum badge */}
                            <div className="flex items-center justify-center gap-1">
                              {fair.website ? (
                                <a href={fair.website} target="_blank" rel="noreferrer"
                                  className="p-1.5 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                                  title={fair.website}>
                                  <ExternalLink className="w-4 h-4" />
                                </a>
                              ) : fair.website_fetch_failed === true ? (
                                <span title="Website URL çekilemedi" className="inline-flex items-center gap-1 text-[10px] font-bold bg-red-50 text-red-500 border border-red-200 px-1.5 py-0.5 rounded-full">
                                  <Link2Off className="w-3 h-3" /> Web Yok
                                </span>
                              ) : fair.source_url ? (
                                <span title="Website henüz çekilmedi" className="inline-flex items-center gap-1 text-[10px] font-bold bg-amber-50 text-amber-500 border border-amber-200 px-1.5 py-0.5 rounded-full">
                                  <Link2 className="w-3 h-3" /> Bekliyor
                                </span>
                              ) : null}
                            {/* Kontak bilgisi badge'leri */}
                            {fair.event_email && (
                              <a href={`mailto:${fair.event_email}`}
                                className="p-1.5 text-gray-400 hover:text-teal-600 rounded-lg hover:bg-teal-50 transition-colors"
                                title={`Event E-mail: ${fair.event_email}`}>
                                <Mail className="w-4 h-4" />
                              </a>
                            )}
                            {fair.organizers && fair.organizers.length > 0 && (
                              <span title={fair.organizers.map(o => o.name).join(', ')}
                                className="inline-flex items-center gap-1 text-[9px] font-bold bg-teal-50 text-teal-700 border border-teal-200 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                                <Users className="w-2.5 h-2.5" />
                                {fair.organizers.length}
                              </span>
                            )}
                            <button onClick={() => { setEditFair(fair); setShowModal(true); }}
                                className="p-1.5 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-blue-50 transition-colors">
                                <Edit3 className="w-4 h-4" />
                              </button>
                              <button onClick={() => setDeleteId(fair.fair_id)}
                                className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                            {/* Tarih scrape durumu */}
                            {fair.site_scrape_status === 'success' && (
                              <span title={`Yöntem: ${fair.site_scrape_method || '?'}`}
                                className="inline-flex items-center gap-1 text-[9px] font-bold bg-emerald-50 text-emerald-600 border border-emerald-200 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                                <CheckCheck className="w-2.5 h-2.5" />
                                {fair.site_scraped_start !== fair.start_date
                                  ? `Tarih Güncellendi`
                                  : `Tarih Aynı`
                                }
                                {fair.site_scraped_at && (
                                  <span className="text-emerald-400 font-normal">
                                    {new Date(fair.site_scraped_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}
                                  </span>
                                )}
                              </span>
                            )}
                            {fair.site_scrape_status === 'no_dates' && (
                              <span className="inline-flex items-center gap-1 text-[9px] font-bold bg-amber-50 text-amber-500 border border-amber-200 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                                <Minus className="w-2.5 h-2.5" /> Tarih Bulunamadı
                              </span>
                            )}
                            {fair.site_scrape_status === 'failed' && (
                              <span className="inline-flex items-center gap-1 text-[9px] font-bold bg-red-50 text-red-500 border border-red-200 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                                <AlertCircle className="w-2.5 h-2.5" /> Erişilemedi
                              </span>
                            )}
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
                <span className="text-sm text-gray-500">
                  {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, fairs.length)} / {fairs.length}
                </span>
                <div className="flex gap-2">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                    className="p-2 text-gray-400 hover:text-gray-700 disabled:opacity-30 rounded-lg hover:bg-gray-100">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const start = Math.max(1, Math.min(page - 2, totalPages - 4));
                    const n = start + i;
                    if (n > totalPages) return null;
                    return (
                      <button key={n} onClick={() => setPage(n)}
                        className={`w-8 h-8 text-sm rounded-lg font-medium ${n === page ? 'bg-blue-600 text-white' : 'text-gray-500 hover:bg-gray-100'}`}>
                        {n}
                      </button>
                    );
                  })}
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                    className="p-2 text-gray-400 hover:text-gray-700 disabled:opacity-30 rounded-lg hover:bg-gray-100">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      {showWebScrape && (
        <WebScrapeModal onClose={() => { setShowWebScrape(false); fetchFairs(); }} />
      )}
      {showEnrich && (
        <WebsiteEnrichModal
          onClose={() => { setShowEnrich(false); fetchFairs(); }}
        />
      )}
      {showContactEnrich && (
        <ContactEnrichModal
          onClose={() => { setShowContactEnrich(false); fetchFairs(); }}
          onMinimize={() => setShowContactEnrich(false)}
        />
      )}

      {/* Floating Mini Progress — Modal kapalıyken arka plan zenginleştirme göstergesi */}
      {!showContactEnrich && contactProgress?.running && (
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          className="fixed bottom-6 right-6 z-40 bg-white rounded-2xl shadow-2xl border border-teal-100 w-80 overflow-hidden cursor-pointer"
          onClick={() => setShowContactEnrich(true)}
          title="Detayları görmek için tıkla"
        >
          {/* Üst başlık */}
          <div className="bg-gradient-to-r from-teal-600 to-emerald-600 px-4 py-2.5 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <RefreshCw className="w-3.5 h-3.5 text-white animate-spin" />
              <span className="text-xs font-bold text-white">Kontak Zenginleştirme Çalışıyor</span>
            </div>
            <span className="text-xs font-bold text-teal-100">
              {contactProgress.total > 0 ? Math.round((contactProgress.done / contactProgress.total) * 100) : 0}%
            </span>
          </div>
          {/* Progress bar */}
          <div className="h-1.5 bg-gray-100">
            <div
              className="h-full bg-gradient-to-r from-teal-500 to-emerald-500 transition-all duration-700"
              style={{ width: `${contactProgress.total > 0 ? (contactProgress.done / contactProgress.total) * 100 : 0}%` }}
            />
          </div>
          {/* İstatistikler */}
          <div className="px-4 py-3">
            <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
              <span>{contactProgress.done?.toLocaleString()} / {contactProgress.total?.toLocaleString()} fuar</span>
              <span className="text-emerald-600 font-semibold">✓ {contactProgress.updated} güncellendi</span>
            </div>
            <div className="grid grid-cols-3 gap-1.5 text-center">
              <div className="bg-blue-50 rounded-lg py-1">
                <p className="text-xs font-bold text-blue-700">{contactProgress.total_with_official_site?.toLocaleString() || '0'}</p>
                <p className="text-[9px] text-blue-400">Resmi Site</p>
              </div>
              <div className="bg-violet-50 rounded-lg py-1">
                <p className="text-xs font-bold text-violet-700">{contactProgress.total_with_email?.toLocaleString() || '0'}</p>
                <p className="text-[9px] text-violet-400">E-mail</p>
              </div>
              <div className="bg-orange-50 rounded-lg py-1">
                <p className="text-xs font-bold text-orange-700">{contactProgress.total_with_organizers?.toLocaleString() || '0'}</p>
                <p className="text-[9px] text-orange-400">Organizatör</p>
              </div>
            </div>
            <p className="text-[10px] text-gray-400 text-center mt-2">Detay için tıkla · Sayfayı kapatabilirsiniz</p>
          </div>
        </motion.div>
      )}
      {showEventseye && (
        <EventsEyeModal
          onClose={() => setShowEventseye(false)}
          onComplete={() => { fetchFairs(); setShowEventseye(false); }}
        />
      )}
      {showModal && (
        <FairModal
          fair={editFair}
          onClose={() => { setShowModal(false); setEditFair(null); }}
          onSave={handleSave}
        />
      )}
      {showAiModal && (
        <AiGenerateModal
          onClose={() => setShowAiModal(false)}
          onGenerated={fetchFairs}
        />
      )}

      {/* Delete Confirm */}
      {deleteId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-bold text-center text-gray-900 mb-2">Fuarı Sil</h3>
            <p className="text-sm text-gray-500 text-center mb-6">Bu fuar kalıcı olarak silinecek. Bu işlem geri alınamaz.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)}
                className="flex-1 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
                İptal
              </button>
              <button data-testid="confirm-delete-fair" onClick={() => handleDelete(deleteId)}
                className="flex-1 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700">
                Evet, Sil
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
